// Illustrative projects (the server's examples/skfitter/<id>/, examples/dft/<id>/ for the DFT module and examples/negf/<id>/
// for NEGF Transport): the list, one example with its guide, "open", and for DFT and NEGF ones "try this" (a new project
// with one thing changed).
import { useQuery } from "@tanstack/react-query";
import { get, post, type Project } from "./api";

export interface ExampleSteps { dft?: string; setup?: string; result?: string; properties?: string;
  model?: string; device?: string; gates?: string; contacts?: string; calc?: string }   // the last five: NEGF

export interface Example {
  id: string; order: number; name: string; material: string; speciality: string[]; summary: string;
  code: "vasp" | "espresso" | "negf"; files: string[]; steps?: ExampleSteps; project_id: string | null;
  guide?: string;
  advanced: boolean;             // its projects run with the Advanced plan's resources (set by an admin)
  product?: "skfitter" | "dft" | "negf" | "properties" | "wannier";
  // DFT illustrative projects: the run's summary, what it is compared with, "try this"
  result?: { gap_eV?: number | null; gap_direct?: boolean | null; fermi_energy_eV?: number | null; seconds?: number | null;
    nprocs?: number | null; total_magnetization?: number | null; site_moments?: number[] | null; qe_version?: string | null;
    // Wannier illustrative projects: the finished run's headline numbers
    num_wann?: number | null; omega_A2?: number | null; band_error_max_eV?: number | null; band_error_rms_eV?: number | null;
    converged?: boolean; warnings?: number; wannier90_version?: string | null; total_s?: number | null };
  recipe?: Record<string, any>;     // Wannier: the recipe of the finished run
  dft_example?: string | null;      // Wannier: the DFT illustrative project whose recipe its DFT run used
  reference?: string; check?: string;
  runs?: { seed: string; title: string }[];   // DFT defect projects: the other runs copied in (host, references, ...)
  tries?: ExampleTry[];
  expect?: string;                  // NEGF: what the calculation shows when it works
  model_source?: string;            // NEGF: where the model comes from
  calc?: string;                    // NEGF: the calculation the project is set up for
  // Properties illustrative projects: the textbook model (or null: a shipped fit), the Materials project it continues,
  // the guide's calculations
  textbook?: string | null;
  counterpart?: string | null;
  calculations?: { calculator: string; note: string }[];
  // Materials ones: what they continue with in Properties, and the Properties project that continues them
  properties_continue?: { what: string; calculations: { calculator: string; inputs?: Record<string, unknown> }[] } | null;
  properties_counterpart?: string | null;
}
export interface ExampleTry { id: string; title: string; about: string }

export const CODE_NAMES: Record<string, string> = { vasp: "VASP", espresso: "Quantum ESPRESSO" };
export const PROPERTIES_EXAMPLES_TITLE = "Properties - Illustrative Projects";

export function useExamples() {
  return useQuery({ queryKey: ["examples"], queryFn: () => get<Example[]>("/examples"), staleTime: 60_000 });
}

export function useExample(id: string | undefined) {
  return useQuery({ queryKey: ["example", id], queryFn: () => get<Example>(`/examples/${id}`), enabled: !!id, staleTime: 60_000 });
}

/** The user's project of this example: the existing one, or a new one with the files and the set-up filled in. */
export const openExample = (id: string) => post<Project>(`/examples/${id}/open`);
/** "Try this": a new DFT project with the example's recipe changed. */
export const tryExample = (id: string, tryId: string) => post<Project>(`/examples/${id}/try/${tryId}`);

/** One line of a DFT illustrative project's result: gap (direct or not) or metal, moments. */
export function resultLine(r: Example["result"]): string {
  if (!r) return "";
  const parts: string[] = [];
  if (r.gap_eV != null) parts.push(r.gap_eV > 0 ? `gap ${r.gap_eV.toFixed(3)} eV${r.gap_direct ? " (direct)" : r.gap_direct === false ? " (indirect)" : ""}` : "metal");
  const sites = (r.site_moments ?? []).filter((m) => Math.abs(m) > 0.3);
  if (sites.length) parts.push(`${(sites.reduce((a, m) => a + Math.abs(m), 0) / sites.length).toFixed(2)} μB per magnetic atom`);
  else if (r.total_magnetization) parts.push(`${r.total_magnetization.toFixed(2)} μB/cell`);
  return parts.join(", ");
}

/** The hint for step `step` (0 DFT data, 1 set-up, 2 result, 3 properties) of an example. */
export function stepHint(steps: ExampleSteps | undefined, step: number): string {
  const key = (["dft", "setup", "result", "properties"] as const)[step];
  return (key && steps?.[key]) || "";
}
